import type { MouseEvent } from 'react';
import React from 'react';
import { Breakpoint, useBreakpoint } from '@noths/polaris-client-ribbons-base';
import { toSentenceCase } from '@noths/polaris-client-utils';

import arrowBack from 'src/assets/svg/arrowBack.svg';
import type { Filter, FilterOptionValue } from 'src/types';
import type { FilterOptionsProps } from '../../containers/FilterOptionsContainer.types';
import type { TreeProps } from '../../containers/TreeContainer.types';
import { FilterOptionsExpandableContent } from './FilterOptionsExpandableContent';
import * as styles from '../../styles/Tree.styles';

export const onClick = (
  event: MouseEvent<HTMLAnchorElement>,
  value: FilterOptionValue,
  depth: number,
  filter: Filter,
  onSelectFilterOption: FilterOptionsProps['onSelectFilterOption'],
) => {
  event.preventDefault();

  onSelectFilterOption({
    filterUid: filter.uid,
    filterOptionValue: value,
    filterOptionDepth: depth,
  });
};

export const Tree = ({
  filter,
  onSelectFilterOption,
  pathWithoutCurrentFilter,
  parentNodes,
  childNodes,
  isExpanded = true,
  onExpandableContentToggle,
}: TreeProps) => {
  const totalBranches = parentNodes.length + childNodes.length;
  const isOnSmallScreen = useBreakpoint() === Breakpoint.S;
  const isExpandable = !isOnSmallScreen && totalBranches > 6 && isExpanded;
  const isTreeRoot = parentNodes.every((x) => x.depth === 1);

  return (
    <FilterOptionsExpandableContent
      filter={filter}
      onExpandableContentToggle={onExpandableContentToggle}
      isExpandable={isExpandable}
    >
      <ul css={styles.list}>
        {parentNodes.map(({ active, value, title, depth }) => (
          <li key={value}>
            <a
              href={`${pathWithoutCurrentFilter}&${filter.uid}=${value}`}
              css={styles.parentNodeLink}
              onClick={(e) => onClick(e, value, depth, filter, onSelectFilterOption)}
              data-is-active={active}
              aria-label={`${title}${active ? ' (currently selected)' : ''}`}
            >
              {!active && !isOnSmallScreen && !isTreeRoot && (
                <img src={arrowBack} alt="" width={16} height={16} />
              )}
              {toSentenceCase(title)}
            </a>
            {active && childNodes.length > 0 && (
              <ul aria-label={`${toSentenceCase(title)} sub-categories`} css={styles.list}>
                {childNodes.map(({ value, depth, title }) => (
                  <li key={value}>
                    <a
                      href={`${pathWithoutCurrentFilter}&${filter.uid}=${value}`}
                      css={styles.childNodeLink}
                      onClick={(e) => onClick(e, value, depth, filter, onSelectFilterOption)}
                    >
                      {toSentenceCase(title)}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </FilterOptionsExpandableContent>
  );
};
